import React from 'react';
import { Zap } from 'lucide-react';

const MAX_KARMA = 10000;
const RADIUS    = 38;
const CIRCUMFERENCE = 2 * Math.PI * RADIUS;

const KarmaPulseCard = ({ karmaBalance = 0, walletMinutes = 0, loading, onTopUp }) => {
  const pct    = Math.min((karmaBalance / MAX_KARMA) * 100, 100);
  const offset = CIRCUMFERENCE * (1 - pct / 100);

  const displayKarma =
    karmaBalance >= 1000
      ? `${(karmaBalance / 1000).toFixed(1)}k`
      : String(karmaBalance);

  return (
    <div
      className="jp-card"
      style={{ padding: '14px 16px' }}
      data-testid="karma-pulse-card"
    >
      {/* Header label */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 12 }}>
        <Zap style={{ width: 11, height: 11, color: 'var(--gold-primary)', flexShrink: 0 }} />
        <span style={{
          fontFamily: 'Montserrat, sans-serif', fontSize: 8, fontWeight: 600,
          color: 'var(--text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.14em',
        }}>
          KARMA BALANCE
        </span>
      </div>

      {/* Ring + stats */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>

        {/* SVG ring — tappable: the whole balance ring opens the store, not
            just the small "Top Up" button below. */}
        <div
          onClick={loading ? undefined : onTopUp}
          role={onTopUp ? 'button' : undefined}
          tabIndex={onTopUp && !loading ? 0 : undefined}
          onKeyDown={(e) => { if (onTopUp && !loading && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); onTopUp(); } }}
          title={onTopUp ? 'Top up Karma' : undefined}
          aria-label={onTopUp ? 'Top up Karma' : undefined}
          style={{
            position: 'relative', width: 90, height: 90, flexShrink: 0,
            cursor: onTopUp && !loading ? 'pointer' : 'default',
          }}
          data-testid="karma-pulse-ring"
        >
          <svg
            width={90} height={90}
            viewBox="0 0 90 90"
            style={{ transform: 'rotate(-90deg)' }}
          >
            {/* Track */}
            <circle
              cx={45} cy={45} r={RADIUS}
              fill="none" stroke="var(--card-border)" strokeWidth={5}
            />
            {/* Progress arc */}
            {!loading && (
              <circle
                cx={45} cy={45} r={RADIUS}
                fill="none"
                stroke="var(--gold-primary)"
                strokeWidth={5}
                strokeLinecap="round"
                strokeDasharray={CIRCUMFERENCE}
                strokeDashoffset={offset}
                style={{ transition: 'stroke-dashoffset 1s ease' }}
              />
            )}
          </svg>

          {/* Center text */}
          <div style={{
            position: 'absolute', inset: 0,
            display: 'flex', flexDirection: 'column',
            alignItems: 'center', justifyContent: 'center',
          }}>
            {loading ? (
              <div className="cs-skeleton" style={{ width: 28, height: 14, borderRadius: 3 }} />
            ) : (
              <>
                <span style={{
                  fontFamily: 'Cinzel, serif', fontSize: 13,
                  color: 'var(--gold-bright)', fontWeight: 600, lineHeight: 1,
                }}>
                  {displayKarma}
                </span>
                <span style={{
                  fontFamily: 'Montserrat, sans-serif', fontSize: 7,
                  color: 'var(--text-tertiary)', letterSpacing: '0.07em', marginTop: 2,
                }}>
                  KARMA
                </span>
              </>
            )}
          </div>
        </div>

        {/* Stats column */}
        <div style={{ flex: 1, minWidth: 0 }}>
          {loading ? (
            <>
              <div className="cs-skeleton" style={{ height: 12, borderRadius: 3, width: '70%', marginBottom: 7 }} />
              <div className="cs-skeleton" style={{ height: 10, borderRadius: 3, width: '50%' }} />
            </>
          ) : (
            <>
              <p style={{
                fontFamily: 'Montserrat, sans-serif', fontSize: 11,
                color: 'var(--text-secondary)', marginBottom: 2,
              }}>
                {walletMinutes} min available
              </p>
              <p style={{
                fontFamily: 'Montserrat, sans-serif', fontSize: 9,
                color: 'var(--text-tertiary)', marginBottom: 12,
              }}>
                {Math.round(pct)}% of 10k
              </p>
              <button
                onClick={onTopUp}
                style={{
                  background: 'none', cursor: 'pointer',
                  border: '1px solid var(--gold-dim)', borderRadius: 6,
                  padding: '5px 14px',
                  fontFamily: 'Montserrat, sans-serif', fontSize: 9,
                  fontWeight: 600, color: 'var(--gold-primary)',
                  transition: 'background var(--transition-fast)',
                }}
                onMouseEnter={e => { e.currentTarget.style.background = 'var(--gold-glow)'; }}
                onMouseLeave={e => { e.currentTarget.style.background = 'none'; }}
                data-testid="karma-topup-btn"
              >
                Top Up
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default KarmaPulseCard;
